/**
 * The artwork generator's decisions, as values — `screens.md` §6, without a browser.
 *
 * ## Why this file exists, stated as a rule rather than as a habit
 *
 * `apps/app`'s suite is `renderToStaticMarkup`. **It fires no events and runs no effects.**
 * So a state reachable only after a click is unreachable by that suite *by construction*,
 * and this project has shipped two defects that lived precisely in the gap between "the
 * component is correct" and "the suite can see it" — a dashboard that rendered nothing for
 * every signed-in user, and an audit-log diff nobody could open.
 *
 * The answer is not more test cleverness. It is that **every decision on this screen is a
 * pure function or an exported component**, and the residue — what genuinely needs an event
 * — is *written down* rather than left implied. That list is {@link C93_FLOWS} at the bottom
 * of this file, and it is part of the deliverable.
 *
 * ## The screen composes no refusal sentence, and this file is where that is kept
 *
 * U17: *"the screen renders refusals from the problem body's `remedy` and `detail`. It never
 * composes its own sentence."* Nothing below authors a refusal string. What it does is
 * **route** — decide which shape of affordance a refusal gets — and it routes on
 * `remedy.action`, which is the wire's own vocabulary, not on a code the screen re-maps to
 * local copy. A switch on `code` with copy beside it would be two sources of truth for one
 * refusal, and the public `/problems/` page is already a third.
 *
 * The copy this file *does* hold is copy about **the product's own settings** — what an
 * X-dimension is, what a watermark is for, what a ruler is for. None of it restates
 * something the API says.
 */
import {
  ARTWORK_ECC_LEVELS,
  ARTWORK_FORMATS,
  ARTWORK_SYMBOLOGIES,
  ARTWORK_WATERMARKS,
  type ArtworkBatchRequest,
  type ArtworkEccLevel,
  type ArtworkFormat,
  type ArtworkPreflightRow,
  type ArtworkSymbology,
  type ArtworkWatermark,
  type PlacementSheetRequest,
  type RemedyAction,
  type WarningCode,
} from "@packagepage/shared";
import type { TimelineStep } from "@packagepage/ui";

import { domainTimeline, WAITING_ON_DNS } from "./domain-timeline.js";

// ---------------------------------------------------------------------------
// What the person is asking for
// ---------------------------------------------------------------------------

/**
 * The generator's controls, as one value.
 *
 * `xDimensionMm` is a **string**, not a number, and that is not laziness about inputs: it is
 * the one field with no default (see {@link EMPTY_SETTINGS}), so "not yet answered" has to
 * be representable, and `0` is a number rather than an absence.
 */
export interface ArtworkSettings {
  readonly symbology: ArtworkSymbology;
  readonly format: ArtworkFormat;
  /** Millimetres, as typed. Empty until the person answers — see {@link EMPTY_SETTINGS}. */
  readonly xDimensionMm: string;
  readonly eccLevel: ArtworkEccLevel;
  /** The batch this code is for, or `null` for *this product, all units*. */
  readonly lot: string | null;
  /**
   * Whether the dual-marking placement sheet comes down beside the file — c96.
   *
   * **A boolean beside the format rather than a fifth value of it**, and that is the screen's
   * half of the ruling: the sheet is a document *about* the symbol, so it is orthogonal to
   * which file the symbol is drawn into. Somebody downloading an SVG and an EPS wants one
   * sheet, not two, and a fifth radio option would have made "the sheet" a thing you get
   * *instead of* artwork.
   */
  readonly placementSheet: boolean;
}

/**
 * The starting position — **and `xDimensionMm` is deliberately blank.**
 *
 * `artworkRequestSchema` states the rule and this file obeys it rather than working around
 * it: *"It is never defaulted: a symbol whose physical size was inferred is a symbol that
 * scans at the wrong size."*
 *
 * There is a second reason, and it is the one that decided the shape of this screen. The
 * only correct default would be `retail-pos`'s target module width, which is a GS1 number
 * transcribed from General Specifications 26.0 Table 5-46 and owned by
 * `packages/barcode`'s `APPLICATION_PRESETS`. **`apps/app` may not import
 * `packages/barcode` at any distance** — a dependency-cruiser rule with an exit code — so a
 * default here would be that number written a second time, in a package that cannot be
 * checked against the first. `screens.md` §6 asks for a slider whose unavailable range is
 * drawn, and drawing it needs the same number.
 *
 * So the floor arrives the way every other fact on this screen arrives: **from the server,
 * in a refusal.** A value below the minimum is refused with `XDIM_BELOW_MINIMUM`, whose
 * remedy carries `minimumXDimensionMm` and `targetXDimensionMm` — and
 * {@link xDimensionFix} turns the second into a one-click button that carries the value, in
 * the mock's own shape. One source of truth, and the person is taught by the gate rather
 * than by a constant this bundle asserted.
 *
 * Recorded in the Drift Log: §6's drawn hatched range is not buildable in this bundle
 * without duplicating that number, and what shipped instead is the refusal that carries it.
 */
export const EMPTY_SETTINGS: ArtworkSettings = {
  symbology: "qr",
  format: "svg",
  xDimensionMm: "",
  eccLevel: "M",
  lot: null,
  // Unticked. The sheet is free and useful and it is still a second file the person did not
  // ask for; §8's *"generated alongside"* is an offer, and a box we ticked for them would be
  // us deciding what lands in their downloads folder.
  placementSheet: false,
};

/** The typed module width, or `null` while it is blank or not a number. */
export function xDimensionOf(settings: ArtworkSettings): number | null {
  const trimmed = settings.xDimensionMm.trim();
  if (trimmed === "") return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * The preflight request these settings ask for, or `null` when there is nothing to ask.
 *
 * `null` is the state *before* a question rather than a question with no answer, which is
 * the same distinction `edge.ts` draws between `unknown` and `unreachable`. A screen that
 * sent a preflight with no module width would be asking the server to evaluate a symbol
 * nobody has sized.
 *
 * **The lot is not on this request, and its absence is correct.** `artworkBatchRequestSchema`
 * carries no `qualifiers` — one batch is one set of render parameters — so a batch-qualified
 * preflight would be asking about a URI the batch endpoint cannot construct. Every gate this
 * screen renders is about the *product*, the *prefix*, the *domain* and the *size*, and none
 * of the six is sensitive to a lot code. The lot reaches the wire on the file request, where
 * it belongs, and {@link encodedUri} is what the screen shows meanwhile.
 */
export function preflightRequestFor(
  productId: string,
  settings: ArtworkSettings,
): ArtworkBatchRequest | null {
  const xDimensionMm = xDimensionOf(settings);
  if (xDimensionMm === null) return null;
  return {
    productIds: [productId],
    symbology: settings.symbology,
    format: settings.format,
    xDimensionMm,
    applicationPreset: "retail-pos",
    eccLevel: settings.eccLevel,
    // This screen's preflight asks about one product and starts nothing. The ZIP is
    // `BatchZipControl`'s request, over the catalogue, and it sets its own flag.
    zip: false,
  };
}

// ---------------------------------------------------------------------------
// What the preflight said
// ---------------------------------------------------------------------------

/**
 * The screen's five positions before a file is asked for — **five, counted from the members
 * below rather than from the sentence that used to say four.**
 *
 * `unasked` is not a loading spinner: it is the honest state of a screen whose one required
 * question has not been answered yet, and it is what renders on arrival. `unavailable` is
 * not `blocked` either — one is a refusal about the request and the other is a refusal a
 * person can act on.
 */
export type PreflightState =
  | { readonly kind: "unasked" }
  | { readonly kind: "loading" }
  /** The preflight itself failed — a refusal about the request, not about the product. */
  | { readonly kind: "unavailable"; readonly detail: string }
  | { readonly kind: "blocked"; readonly row: ArtworkPreflightRow }
  | { readonly kind: "ready"; readonly row: ArtworkPreflightRow };

/**
 * Read one preflight answer.
 *
 * A row that names nothing (`eligible: false` with no `refused`) is `unavailable` rather
 * than `blocked`: `evaluateArtworkBatch` produces that shape for a product id it could not
 * load, which is a fact about the *request* and has no remedy for a person to act on. Giving
 * it the blocked treatment would render a gate card with no sentence in it.
 */
export function preflightState(input: {
  readonly request: ArtworkBatchRequest | null;
  readonly loading: boolean;
  readonly row: ArtworkPreflightRow | undefined;
  readonly errorDetail: string | null;
}): PreflightState {
  if (input.request === null) return { kind: "unasked" };
  if (input.errorDetail !== null)
    return { kind: "unavailable", detail: input.errorDetail };
  if (input.loading || input.row === undefined) return { kind: "loading" };
  if (input.row.eligible) return { kind: "ready", row: input.row };
  if (input.row.refused === undefined) {
    return {
      kind: "unavailable",
      detail:
        "This product could not be found in this organisation, so there was nothing to " +
        "check. Nothing was generated.",
    };
  }
  return { kind: "blocked", row: input.row };
}

// ---------------------------------------------------------------------------
// How a refusal is shaped on screen — routed on the act, never on the code
// ---------------------------------------------------------------------------

/**
 * What kind of affordance a remedy gets, per act.
 *
 * **Eight branches for eight gate codes, expressed as seven acts plus one absence** — and
 * the count is the point rather than trivia. `REMEDY_ACTIONS` has seven members and
 * `GATE_CODES` has eight, because two codes share the `none` act while saying two different
 * true things in their own `label`. The ninth thing that can arrive here is §8.1 row 3's
 * `INVALID_CHECK_DIGIT`, which carries **no** remedy at all, and {@link remedyShapeOf}
 * answers `"stated"` for it.
 *
 * `satisfies Record<RemedyAction, RemedyShape>` is what makes this a census rather than a
 * list: an act with no entry does not compile, and an entry for an act
 * `REMEDY_ACTIONS` has never heard of does not either. That is how a seventh act arriving in
 * a later phase becomes a compile error here instead of a gate rendering as a bare sentence
 * with no button.
 */
export type RemedyShape =
  /** The label is the whole remedy. Nothing on this screen can perform it. */
  | "stated"
  /** A button this screen can press, right here, without navigating. */
  | "inline-action"
  /** The full blocked-domain treatment: timeline, recheck, and the hatch beneath them. */
  | "domain-wait"
  /** A value the person can accept in one click — the fix carries the number. */
  | "value"
  /** A screen elsewhere in the dashboard performs it. The label, then a link to that screen. */
  | "link";

/**
 * **Five shapes over seven acts.**
 *
 * `verify_prefix` is a `"link"` to Settings → Organisation, where the claim and the upload live
 * since U25. It was `"stated"` until then, because a link to a route that did not exist is c29's
 * *"remedy that does not exist"*.
 *
 * `await_prefix_review` stays `"stated"`: its reader is disputed, and the channel is support.
 * `none` is `"stated"` because §8.1 says so — `gate.ts` argues why `none` is a member.
 */
export const REMEDY_SHAPES = {
  none: "stated",
  wait_for_domain: "domain-wait",
  verify_prefix: "link",
  await_prefix_review: "stated",
  raise_x_dimension: "value",
  publish_product: "inline-action",
  restore_product: "inline-action",
} as const satisfies Record<RemedyAction, RemedyShape>;

/** How this refusal is shaped. `"stated"` for a body with no remedy — see the docblock. */
export function remedyShapeOf(row: ArtworkPreflightRow): RemedyShape {
  const action = row.refused?.remedy?.action;
  return action === undefined ? "stated" : REMEDY_SHAPES[action];
}

/**
 * The one-click size fix, when the refusal is the X-dimension one.
 *
 * The button carries the corrected value **as its label**, which is `components.md`'s rule
 * for every fix in the product — *"the fix button carries the corrected value so it doubles
 * as the explanation"* — and the number is the server's `targetXDimensionMm`, never a
 * constant this bundle holds.
 *
 * Returns `null` for every other refusal, including for `XDIM_BELOW_MINIMUM` arriving
 * without a remedy, which cannot happen through `gateRefusal` and is not worth a crash if it
 * ever does.
 */
export function xDimensionFix(
  row: ArtworkPreflightRow,
): { readonly label: string; readonly value: number } | null {
  const remedy = row.refused?.remedy;
  if (remedy?.action !== "raise_x_dimension") return null;
  return {
    label: `Set ${String(remedy.targetXDimensionMm)} mm`,
    value: remedy.targetXDimensionMm,
  };
}

// ---------------------------------------------------------------------------
// The blocked-domain state — `screens.md` §6's longest row
// ---------------------------------------------------------------------------

/**
 * The domain timeline's four steps, for the **inline** copy of it this screen carries.
 *
 * ## The vocabulary moved to `domain-timeline.ts` at c144/C4
 *
 * c92 wrote this list inline and named its own trigger — *"the steps belong in one exported
 * constant the day the second consumer exists."* The domain-setup screen is that consumer,
 * so the labels and the state derivation are now shared and this function supplies only what
 * is true of THIS screen: a domain that has never served, waiting on DNS, and four `detail`
 * strings a reader deciding whether to wait for a print file needs.
 *
 * ## Four steps, and the fourth one's name is a reconciliation
 *
 * `components.md` names the fourth step **"Answering scans"** and says it is *"the moment
 * `first_served_at` is written — that timestamp permanently unblocks printing for the
 * domain."* The artwork mock names the same step **"Serving"**, with *"Download unlocks
 * here"* underneath it. Two names for one step, in one handoff.
 *
 * The label is `components.md`'s and the detail is the mock's, and that is not a compromise
 * — it is what `TimelineStep.detail` is for. *"One component, two screens"* is the
 * component spec's own rule, so the **steps** have to read identically on the domain-setup
 * screen Phase 4 builds and here; what differs is why this screen's reader cares, which is
 * exactly the per-occurrence field. Recorded in the Drift Log.
 *
 * ## What is not here, and it is the honest half
 *
 * The mock offers *"Email me the moment it's live"* and `screens.md` §6 lists a notify-me
 * that *"unlocks this download"*. **There is no mail transport anywhere in this repository**
 * — measured, not assumed — so a form promising an email is a remedy that does not exist,
 * which c29 ruled is worse than silence. The auto-recheck is real and ships; the notify-me
 * does not, and its absence is stated on screen rather than mimed with a dead input.
 */
export function dnsTimelineSteps(hostname: string): readonly TimelineStep[] {
  // The SHARED derivation, given this screen's one moment stated as facts — never a
  // special case inside it. If the artwork screen kept its own state logic while importing
  // the constant, the separation would be duplication with extra steps.
  return domainTimeline(WAITING_ON_DNS, {
    added: `${hostname} is on your account and is the address your codes would carry.`,
    record:
      "This is the step holding things up, and it depends on your DNS provider rather " +
      "than on us.",
    certificate: "Issued automatically once the record resolves.",
    serving: "Download unlocks here, permanently — this step is never taken back.",
  });
}

/**
 * The two exits from the blocked state, **in the order they must appear.**
 *
 * The mock's rationale is explicit and is a layout instruction rather than a preference:
 * *"The two exits are stacked, not side by side: waiting is the better outcome for nearly
 * everyone, so it gets the primary button and the top position. The escape hatch is
 * available, plainly labelled, and second."* §6 says the same thing in four words — *"escape
 * hatch **below** those two"*.
 *
 * So the order is a value, and a test can assert it by index rather than by reading a
 * stylesheet. `waiting` is first; `escape` is last; nothing goes after `escape`.
 */
export const BLOCKED_DOMAIN_EXITS = ["waiting", "escape"] as const;
export type BlockedDomainExit = (typeof BLOCKED_DOMAIN_EXITS)[number];

/** How often the blocked state re-asks. The mock's number, and it is a real interval. */
export const RECHECK_INTERVAL_MS = 30_000;

// ---------------------------------------------------------------------------
// §8.1's warn-only set
// ---------------------------------------------------------------------------

/**
 * What each warning does to this screen — **two artwork warnings, two treatments.**
 *
 * `DOMAIN_EVER_LIVE_UNHEALTHY` is §6's *"warn, domain ever-live but currently failing
 * (typed confirmation, not a block)"*, and it is the one warning in the product that gates
 * an act. `XDIM_ABOVE_RECOMMENDED` is *"wasteful, never broken"* and gates nothing.
 *
 * The other two entries in `WARNINGS` belong to links and recalls and never reach this
 * screen; the map is `Partial` for that reason rather than out of caution, and
 * {@link warningTreatment} answers `"advise"` for anything not named — which is the safe
 * direction, because the wrong answer there is a sentence shown without friction rather than
 * an act performed without one.
 */
export type WarningTreatment = "advise" | "confirm-before-download";

export const ARTWORK_WARNING_TREATMENTS = {
  DOMAIN_EVER_LIVE_UNHEALTHY: "confirm-before-download",
  XDIM_ABOVE_RECOMMENDED: "advise",
} as const satisfies Partial<Record<WarningCode, WarningTreatment>>;

export function warningTreatment(code: string): WarningTreatment {
  return Object.hasOwn(ARTWORK_WARNING_TREATMENTS, code)
    ? ARTWORK_WARNING_TREATMENTS[code as keyof typeof ARTWORK_WARNING_TREATMENTS]
    : "advise";
}

/** Does anything in this row's warnings demand the typed confirmation before a download? */
export function requiresPrintConfirmation(row: ArtworkPreflightRow): boolean {
  return row.warnings.some((w) => warningTreatment(w.code) === "confirm-before-download");
}

// ---------------------------------------------------------------------------
// The typed confirmations — two of them now, and they are deliberately different
// ---------------------------------------------------------------------------

/**
 * The word typed to print against a domain that has served before and is failing now.
 *
 * ## Why this is not the GTIN, when the retire dialog's challenge is
 *
 * `components.md` says the typed challenge is *"**the GTIN**, not the word 'delete' — typing
 * the number forces the user to read the identifier of the thing they are destroying."*
 * That reasoning is about **which thing** is being destroyed, and it is exactly right for
 * retiring a product.
 *
 * This confirmation is not about which thing. The product is not in doubt — it is on the
 * screen, and the download confirmation restates it afterwards. What is in doubt is whether
 * the person accepts a **risk**: their domain is correctly delegated, has served
 * continuously, and failed our last probe. The mock's rationale weighs it and lands on four
 * letters: *"a seatbelt, not a wall. Typing four letters is enough friction to stop an
 * accidental click without stranding someone against a deadline."* A fourteen-digit challenge
 * against a print deadline is the friction that converts somebody to the escape hatch, which
 * is the worst outcome available here and the reason §8.1 refuses to block this row at all.
 *
 * `components.md`'s *"currently exactly one"* irreversible act is therefore two from this
 * commit. Recorded in the Drift Log with this argument, so a contrary ruling is one edit.
 */
export const PRINT_CHALLENGE = "PRINT";

export interface Challenge {
  readonly satisfied: boolean;
  /** Index of the first character that does not match, or `null` while a prefix is right. */
  readonly firstWrongIndex: number | null;
  readonly remaining: number;
}

/**
 * Check a typed challenge against the word.
 *
 * Deliberately the same shape as `retire-challenge.ts`'s, and deliberately **not** shared
 * with it: that one compares against a value read off a row and this compares against a
 * constant, and folding them would produce one function with a mode flag standing where two
 * different acts are. Nothing is trimmed and nothing is case-folded — see `retire-challenge`
 * for the argument, which is that a challenge that accepts `print ` accepts a paste.
 */
export function checkPrintChallenge(typed: string): Challenge {
  const satisfied = typed === PRINT_CHALLENGE;
  let firstWrongIndex: number | null = null;
  for (let i = 0; i < typed.length; i += 1) {
    if (typed[i] !== PRINT_CHALLENGE[i]) {
      firstWrongIndex = i;
      break;
    }
  }
  return {
    satisfied,
    firstWrongIndex,
    remaining: Math.max(0, PRINT_CHALLENGE.length - typed.length),
  };
}

/**
 * The escape hatch's consent, as a value.
 *
 * §8.3: the cost *"must be disclosed at the moment, not buried"*, and the mock's proceed
 * button is *"quiet and disabled until the checkbox is ticked"* while the recommended action
 * — waiting — is the primary one. Both facts are decisions about a boolean, so both are
 * decidable without a browser.
 */
export function escapeHatchReady(acknowledged: boolean): boolean {
  return acknowledged;
}

// ---------------------------------------------------------------------------
// The watermarks, and the one thing this screen may say about them
// ---------------------------------------------------------------------------

/**
 * Why each mark is on the file — **the reason, never the mark's own words.**
 *
 * The glyphs a watermark draws live in `packages/barcode`'s `WATERMARK_TEXT` and are not
 * repeated here: this bundle cannot import that package, and transcribing a string it owns
 * would be the two-spellings failure one level down from the one U17 is about. What this
 * holds is product policy — *why* the mark exists — which is a sentence about the platform
 * rather than about the file, and it has no other author.
 *
 * `satisfies Record<ArtworkWatermark, …>` makes the census total: a third mark added to
 * `packages/barcode` and transcribed into `ARTWORK_WATERMARKS` does not compile until it has
 * a sentence here, which is the failure direction that matters — a mark rendering as a bare
 * wire constant in front of a customer.
 *
 * Both sentences are §8.2's own framing. The PNG one is *"a format-safety device, not a
 * nag"*, and it names the tier explicitly because the design's acceptance note does: *"a
 * $599/mo customer sees exactly this."* The prefix one is invariant 4 — a trust gate, not a
 * price gate — and it deliberately does not mention plans at all.
 */
export interface WatermarkNotice {
  readonly headline: string;
  readonly body: string;
}

export const WATERMARK_NOTICES = {
  "raster-preview": {
    headline: "This PNG is a preview, at every tier",
    body:
      "PNG is a screen format. At print size it either resamples or aliases the module " +
      "edges, and a scanner reads the difference — so every PNG we produce carries this " +
      "mark, on every plan including the largest. For anything going to a printer, use " +
      "the SVG, PDF or EPS. All three are free at every tier.",
  },
  "unverified-prefix": {
    headline: "Watermarked until your company prefix is verified",
    body:
      "This is not about your plan and no plan lifts it. It is how we can tell a retailer " +
      "that every unwatermarked packagepage code belongs to the company that printed it. " +
      "Verifying the prefix clears the mark from every format.",
  },
} as const satisfies Record<ArtworkWatermark, WatermarkNotice>;

/** The notices for a set of marks, in `ARTWORK_WATERMARKS`' order rather than the wire's. */
export function watermarkNotices(
  marks: readonly ArtworkWatermark[],
): readonly WatermarkNotice[] {
  return ARTWORK_WATERMARKS.filter((kind) => marks.includes(kind)).map(
    (kind) => WATERMARK_NOTICES[kind],
  );
}

// ---------------------------------------------------------------------------
// What is about to be encoded, and what was downloaded
// ---------------------------------------------------------------------------

/**
 * The path a lot-qualified code would carry, appended to what the preflight said.
 *
 * **The origin is never built here.** The preflight's `digitalLinkUri` is the server's own
 * answer to *which host* — the organisation's custom domain or the platform apex — and a
 * screen that assembled an origin would be right for most organisations and wrong for every
 * one with a domain, silently, on the line whose whole job is saying what goes on the label.
 * So this only ever *extends* that URI with the qualifier the person picked, in the `/10/`
 * spelling GS1 requires.
 *
 * `digitalLinkPath` is not reused for this because it builds a whole path from a GTIN, and
 * the GTIN is already inside the URI the server sent. Re-deriving it would be a second
 * spelling of a string that arrived correct.
 */
export function encodedUri(row: ArtworkPreflightRow, lot: string | null): string | null {
  if (row.digitalLinkUri === undefined) return null;
  return lot === null || lot === ""
    ? row.digitalLinkUri
    : `${row.digitalLinkUri}/10/${encodeURIComponent(lot)}`;
}

/**
 * What the download confirmation states — §6: *"format, physical size, symbology and GTIN"*.
 *
 * Four facts, in one value, so the assertion is about a value rather than about a rendered
 * paragraph. The mock's argument for the whole state is worth keeping beside it: *"A user
 * who downloads the wrong product's artwork finds out here, not at the print shop."*
 *
 * `size` is `null` when the response carried no geometry. It is stated as absent rather than
 * guessed, for {@link ArtworkGeometry}'s reason — a size this screen invented would be a
 * measurement, and a wrong one is worse on this state than on any other.
 */
export interface DownloadConfirmation {
  readonly gtin14: string;
  readonly symbology: string;
  readonly format: string;
  readonly size: string | null;
  readonly filename: string;
  readonly marks: readonly WatermarkNotice[];
  /**
   * The placement sheet's filename when one came down beside the artwork, else `null` — c96.
   *
   * **A fifth fact on a state §6 specifies as four, and it is the same argument the four
   * make.** The mock's reason for the whole receipt is *"a user who downloads the wrong
   * product's artwork finds out here, not at the print shop"* — and two files landing from
   * one click is precisely the thing a person would otherwise discover by finding a second
   * PDF they cannot account for. `null` rather than a boolean, because the useful form of
   * *"the sheet came too"* is the name it came under: both files are named for the same GTIN
   * and a PDF artwork's name differs from the sheet's by one word.
   */
  readonly sheetFilename: string | null;
}

/** The label a person reads for a symbology. Never the wire token on its own. */
export const SYMBOLOGY_LABELS = {
  qr: "QR Code powered by GS1",
  datamatrix: "GS1 Data Matrix",
} as const satisfies Record<ArtworkSymbology, string>;

/** What each format is *for*, in the printer's terms — `components.md`'s "real words". */
export const FORMAT_LABELS = {
  svg: "SVG — vector, what your printer usually wants",
  pdf: "PDF — vector, print-ready at size",
  eps: "EPS — vector, for older prepress workflows",
  png: "PNG — watermarked preview. Raster is never a print deliverable.",
} as const satisfies Record<ArtworkFormat, string>;

/**
 * The placement sheet's control, in the words the screen shows.
 *
 * **A checkbox in the file list**, which is what §8's *"generated alongside"* means on this
 * screen's existing grain: `settings.format` is a radio over four formats and stays one, and
 * ticking this fires a sibling request. The design package's artwork mock draws it exactly
 * here.
 *
 * ## It is offered to unverified prefixes too, and that is the line a reader will suspect
 *
 * §8.1 row 2 withholds the three print-ready vectors from a GTIN whose company prefix we have
 * not verified — so on this screen a person can be reading a `PREFIX_NOT_VERIFIED` refusal
 * with this box still available beside it, which looks like a hole. It is not.
 *
 * **The sheet carries no symbol, so row 2 has nothing to withhold.** `placement.ts` draws a
 * dashed quiet-zone keep-out and a filled footprint — two rectangles — and reads the matrix
 * for dimensions and symbology name only. Cut the footprint out of a printed sheet and you
 * have a grey box. Row 2's mechanism is format-scoped precisely because *"a vector watermark
 * is strippable geometry, so withholding the format is the only defence that matches the
 * harm"*, and there is no geometry here to strip a watermark from or hand to a plate-maker.
 *
 * §8.2 goes further and singles the sheet out as the artefact gating would make the product
 * **less** correct: *"It is a conformance-safety artifact (50 mm dual-marking rule, quiet-zone
 * keep-outs, HRI rules). Gating it would gate correctness."* An unverified brand about to send
 * a pack to press is exactly who most needs to know where the code goes.
 *
 * Ruled 2026-08-21, [[2026-08-21 The Placement Sheet Gets Its Own Door]] §1, and the ruling's
 * condition is mechanical rather than a promise: `packages/barcode`'s suite holds two matrices
 * of identical dimensions and different module content to byte-identical sheets. The day that
 * goes red, this paragraph is false and the box comes off.
 */
export const PLACEMENT_SHEET_LABEL =
  "Placement sheet — where the code goes on the pack: the 50 mm dual-marking rule, " +
  "quiet-zone keep-outs drawn to scale, and the HRI rule. Free at every tier.";

/**
 * The sheet request these settings ask for, or `null` when there is nothing to ask.
 *
 * `null` in the same two cases {@link preflightRequestFor} returns it for — no module width
 * yet — plus the obvious one: the box is unticked. Returning a request nobody asked for and
 * relying on the caller not to send it is how a second file reaches somebody's downloads
 * folder by accident.
 *
 * **The lot IS on this request, unlike the preflight's.** The sheet prints the encoded
 * Digital Link URI on the page and a lot-qualified code is a different URI, so a sheet built
 * without it would carry a line that does not match the artwork beside it. `preflightRequestFor`
 * omits it because `artworkBatchRequestSchema` has no `qualifiers` member to put it in; this
 * schema does, for the same reason `artworkRequestSchema` does.
 */
export function placementSheetRequestFor(
  productId: string,
  settings: ArtworkSettings,
): PlacementSheetRequest | null {
  if (!settings.placementSheet) return null;
  const xDimensionMm = xDimensionOf(settings);
  if (xDimensionMm === null) return null;
  return {
    productId,
    symbology: settings.symbology,
    xDimensionMm,
    applicationPreset: "retail-pos",
    eccLevel: settings.eccLevel,
    escapeHatch: false,
    ...(settings.lot === null ? {} : { qualifiers: { lot: settings.lot } }),
  };
}

export function downloadConfirmation(input: {
  readonly gtin14: string;
  readonly symbology: ArtworkSymbology;
  readonly format: ArtworkFormat;
  readonly xDimensionMm: number;
  readonly widthMm: number | null;
  readonly heightMm: number | null;
  readonly filename: string;
  readonly watermarks: readonly ArtworkWatermark[];
  /** The sheet's name if one was generated. Absent and `null` both mean none came. */
  readonly sheetFilename?: string | null;
}): DownloadConfirmation {
  return {
    gtin14: input.gtin14,
    symbology: SYMBOLOGY_LABELS[input.symbology],
    format: input.format.toUpperCase(),
    size:
      input.widthMm === null || input.heightMm === null
        ? null
        : `${String(input.widthMm)} × ${String(input.heightMm)} mm · X ${String(
            input.xDimensionMm,
          )} mm`,
    filename: input.filename,
    marks: watermarkNotices(input.watermarks),
    sheetFilename: input.sheetFilename ?? null,
  };
}

// ---------------------------------------------------------------------------
// The vocabularies the controls offer
// ---------------------------------------------------------------------------

/**
 * The formats offered, and **all four are offered even where three are refused.**
 *
 * §8.1 row 2 blocks the three vectors for an unverified prefix and leaves the PNG. The
 * instinct is to hide the three; the instinct is wrong, and `components.md`'s plan-gate rule
 * is the one that governs — *"never hidden. Always: what the feature is, why it costs money,
 * one honest non-upgrade path where one exists."* A format that vanished would leave the
 * person unable to tell whether we produce EPS at all. Choosing one produces the refusal,
 * which carries the reason and the remedy, which is the whole point of the gate.
 */
export const FORMAT_CHOICES = ARTWORK_FORMATS;
export const SYMBOLOGY_CHOICES = ARTWORK_SYMBOLOGIES;

/**
 * The ECC levels, offered on QR only.
 *
 * Data Matrix has no such choice — ISO/IEC 16022 fixes ECC200 — and `artworkRequestSchema`
 * accepts the field and ignores it there rather than refusing, so this returns an empty list
 * and the control does not render. Offering a setting that changes nothing is the same class
 * of dishonesty as a disabled button naming an unbuilt feature.
 */
export function eccChoices(
  symbology: ArtworkSymbology,
): readonly ArtworkEccLevel[] | readonly [] {
  return symbology === "qr" ? ARTWORK_ECC_LEVELS : [];
}

/**
 * The application preset control, and why there is not one.
 *
 * §8.1 #4 makes the below-minimum block *"overridable only by deliberately switching to a
 * declared non-POS application — an application choice, not a checkbox"*, and the mock draws
 * a **Change the application** button beside the refusal. `ARTWORK_APPLICATION_PRESETS` has
 * **one** member, so there is no second application to switch to and that button would be a
 * control whose only outcome is the state the person is already in.
 *
 * So the override is not rendered, and this constant is why — stated where somebody
 * reaching for it will find it, rather than as an absence they have to explain. The day a
 * second preset ships, the chooser is a real control and this comment is its trigger.
 * Recorded in the Drift Log.
 */
export const APPLICATION_PRESET_COUNT = 1;

// ---------------------------------------------------------------------------
// The residue — what genuinely needs an event, listed rather than implied
// ---------------------------------------------------------------------------

/**
 * **Everything on this screen that `renderToStaticMarkup` cannot reach, named.**
 *
 * The kickoff's rule, written out: *"anything only an event can produce is listed for c93's
 * flows instead — write the list; do not leave it implied."* Each entry is one E2E
 * assertion, and each one is here because the transition itself is the claim — not because
 * the component behind it is untested. Every value each of these transitions produces is a
 * pure function above, exercised directly.
 *
 * Exported rather than a comment so that `grep` finds it from c93's spec file and so the
 * count is a number a test can assert rather than a paragraph somebody re-reads.
 */
export const C93_FLOWS = [
  "the fix button's click actually writes targetXDimensionMm into the size field",
  "the check-digit fix button's click actually writes params.corrected into the GTIN field",
  "typing PRINT enables the download-anyway button, and a wrong prefix does not",
  "ticking the escape-hatch acknowledgement enables the quiet proceed button",
  "the auto-recheck fires on its interval and the block clears once the domain answers",
  "the preview PNG arrives and renders inside the true-scale canvas",
  "the download itself: the blob reaches the browser and the confirmation states four facts",
  // c96, and c93 is where it is asserted: the checkbox is a second REQUEST, so no static
  // render reaches it and the only honest proof is a browser holding two files.
  "ticking the placement sheet downloads a second file, and the PDF arrives beside the artwork",
  "the inline publish button's click publishes and the block clears",
] as const;
